import _ from 'lodash';
import {getMaxWellTipPositionValue} from 'lib/PipettingProfileItemHelper';
import {isInsertLocationOnCaddy} from 'lib/virtualTrayHelper';
import {parse} from 'papaparse';
import {CARD_DEFAULT_PROPERTIES, WASTE_BIN_LABWARE_ID} from '../../constants/cardStateEnum';
import {
  TaskType,
  SubCardType,
  CardType,
  CardInvalidType,
  SerialDilutionStep,
  TrayTypeKey,
} from '../../constants/dataEnum';
import {getTaskGroups} from '../../contents/ProtocolEditor/VirtualTray/labwareHelper';
import jsonCopy from '../../lib/jsonCopy';
import {generateNormalizationPreviewData, getCSVParsedData} from '../../lib/normalizationHelper';
import {cloneAndMapObject, filter, findKeyByValue, mapObject} from '../../lib/objectHelper';
import {
  ALLOWED_EMPTY_VALUE,
  CSV_CELL_KEYS,
  CsvCellKeysEnum,
  dequote,
  ESCAPE_CHAR,
  extractValueInPlateListRow,
  isIgnoredCell,
  PlateListCell,
} from '../../lib/protocolConversionHelper';
import {generateSdRawTasksAndSummary, mapToSerialDilutionStep} from '../../lib/serialDilutionHelper';
// eslint-disable-next-line import/no-cycle
import {getNewCardName} from './commonTaskListBusiness';
import {isPoolingCardValid} from '../../lib/cardAndTaskHelper';

const parseToStringLiteral = (value) => {
  let newValue = value;

  if (value && typeof value === 'string') {
    newValue = newValue.replaceAll(ESCAPE_CHAR.QUOTE.char, ESCAPE_CHAR.QUOTE.string);
    newValue = newValue.replaceAll(ESCAPE_CHAR.COMA.char, ESCAPE_CHAR.COMA.string);
  }

  return newValue;
};

export const adjustSerialDilutionTasksNumber = (sdRawTasks, tasks, serialDilutionCard) => {
  const newTasks = {...tasks};
  const tasksArray = Object.values(tasks);

  const getWellPositionKey = (task) => `${task.labwareID}-${task.row}-${task.column}`;

  // mapping of labwareID, row and column (key) with task number (value). ex: { ['100-A-2']: 1 }
  const positionMapping = mapObject(sdRawTasks, (rawTask) => ({[getWellPositionKey(rawTask)]: rawTask.number}));

  let currentTaskNumber = 1;

  for (let i = 0; i < tasksArray.length; i += 1) {
    if (!serialDilutionCard.tasks.includes(tasksArray[i].ID)) continue;
    const task = tasksArray[i];

    const positionKey = getWellPositionKey(task);
    const taskNumberByWellPosition = positionMapping[positionKey];

    const isChangeTaskNumber =
      taskNumberByWellPosition &&
      taskNumberByWellPosition !== currentTaskNumber &&
      (task.type === TaskType.ASPIRATE ||
        (task.type === TaskType.DISPENSE && currentTaskNumber === SerialDilutionStep.SET_SAMPLE_TARGET));

    if (isChangeTaskNumber) {
      currentTaskNumber = taskNumberByWellPosition;

      const prevTask = tasksArray[i - 1];
      // pick tip task haven't got the correct task number because the number is defined by the upcoming aspirate task
      if (prevTask.type === TaskType.PICK_TIP) {
        newTasks[prevTask.ID].number = currentTaskNumber;
      }
    }

    newTasks[task.ID].number = currentTaskNumber;
  }

  return newTasks;
};

const processDiluentTargetTasks = (diluentTargetTasks, replicateWell) =>
  diluentTargetTasks.map((task, index) => {
    const updatedTask = {};

    if (index === 0) {
      if (replicateWell > 1 && (task.columnTipCount === 1 || task.rowTipCount === 1)) {
        updatedTask.rowTipCount = task.rowTipCount === 1 ? task.rowTipCount * replicateWell : task.rowTipCount;
        updatedTask.columnTipCount =
          task.columnTipCount === 1 ? task.columnTipCount * replicateWell : task.columnTipCount;
      }
    }

    return {...task, ...updatedTask};
  });

const recoverSerialDilutionCardProperties = (serialDilutionProperty, serialDilutionCard, tasks) => {
  const {
    diluentSourceId,
    sampleSourceId,
    sampleConcentrationIds,
    diluentTargetIds,
    step,
    reuseTips,
    isSetSampleToAllSteps,
    diluentTargetProperties,
    replicateWell,
  } = serialDilutionProperty;

  // #region sdRawTasks processing
  const diluentSourceTask = tasks[diluentSourceId];
  const sampleSourceTask = tasks[sampleSourceId];
  const sampleConcentrationTask = Object.values(tasks).filter((task) => sampleConcentrationIds?.includes(task.ID));
  const diluentTargetTasks = _.isArray(diluentTargetIds)
    ? diluentTargetIds.map((id) => tasks[id])
    : [tasks[diluentTargetIds]];

  const stepProperties = JSON.parse(diluentTargetProperties);
  const equalDiluentVolume = [...new Set(stepProperties.map((el) => el.stepDiluentVolume))].length === 1;

  const aliquotVolume = sampleConcentrationTask[0] ? sampleConcentrationTask[0].volume : 0;
  const diluentVolume =
    diluentSourceTask?.volume ||
    (!diluentSourceTask?.volume && equalDiluentVolume ? stepProperties[0].stepDiluentVolume : 0);

  const mappedSerialDilutionTasks = mapToSerialDilutionStep(
    diluentSourceTask,
    sampleSourceTask,
    sampleConcentrationTask,
    processDiluentTargetTasks(diluentTargetTasks, replicateWell),
  );

  const {sdRawTasks, sdRawTasksSummary} = generateSdRawTasksAndSummary(mappedSerialDilutionTasks, step);

  return {
    newSerialDilutionProperties: {
      ...serialDilutionCard,
      sdProperty: {
        aliquotVolume,
        diluentVolume,
        sampleVolume: sampleSourceTask?.volume ? sampleSourceTask?.volume : 0,
        equalDiluentVolume,
        isSetSampleToAllSteps,
        reuseTips,
        step,
        stepProperties,
        replicateWell,
      },
      sdRawTasks,
      sdRawTasksSummary,
    },
    updatedTasks: adjustSerialDilutionTasksNumber(sdRawTasks, tasks, serialDilutionCard),
  };
};

const recoverNormalizationCardProperties = (normalizationProperty, normalizationCard, labwareState) => {
  const {
    csvRawData,
    csvFileName,
    csvSelectedIndex,
    currentVolume,
    delimiter,
    wellIncrementPattern,
    targetConcentration,
    type,
    diluentLabwareId,
    diluentWellPosition,
    sampleLabwareId,
  } = normalizationProperty;

  const csvParsedData = getCSVParsedData(csvRawData, delimiter);

  // set cards property
  const normalizationState = {
    csvData: csvParsedData.data,
    csvRawData,
    csvFileName,
    currentVolume,
    delimiter,
    type,
    selectedColumnIndex: csvSelectedIndex[1],
    diluentWell: {
      row: diluentWellPosition[0],
      column: diluentWellPosition[1],
    },
    sampleLabwareID: sampleLabwareId.toString(),
    diluentLabwareID: diluentLabwareId.toString(),
    targetConcentration,
    wellIncrementPattern,
    startRow: csvSelectedIndex[0],
    totalRow: csvParsedData.data.length,
  };

  const previewData = generateNormalizationPreviewData(normalizationState, labwareState);
  normalizationState.previewData = previewData;

  return {...normalizationCard, normalizationState};
};

export const recoverPoolingCardProperties = (poolingCard, tasks, labwareState) => {
  const {tasks: currentCardTasks} = poolingCard;

  const aspirateTask = filter(tasks, (task) => currentCardTasks.includes(task.ID) && task.type === TaskType.ASPIRATE);

  const taskGroups = getTaskGroups(aspirateTask, labwareState);
  const {rowTipCount, columnTipCount} = taskGroups[taskGroups.length - 1];
  const isCardValid = isPoolingCardValid(poolingCard, tasks);

  return {
    ...poolingCard,
    invalidTypes: isCardValid ? [] : [CardInvalidType.UNFINISHED],
    poolingHelperState: {
      allowedTips: {row: rowTipCount, column: columnTipCount},
      groupedByColumn: columnTipCount <= rowTipCount,
      taskGroups,
    },
  };
};

// will be used for convertCsvToCardState and OpenProtocol
export const getCardStateFromCardObject = (cardObject, cardsDataFromCSV, tasks, labwareState) => {
  const {card, serialDilution, normalization} = cardObject;

  let newTasks = {...tasks};
  const newCards = {};

  Object.values(cardsDataFromCSV).forEach((csvCard) => {
    const {id: cardDbID, cardId: cardID, tasks: unusedCardObjTasks, isReuseTips, ...rest} = card[csvCard.ID] || {};

    newCards[cardID] = cloneAndMapObject({...csvCard, ...rest}, CARD_DEFAULT_PROPERTIES, true);
    const currentCard = newCards[cardID];

    if (currentCard.type === CardType.SIMPLE_TRANSFER) return;

    if (currentCard.type === CardType.SERIAL_DILUTION) {
      const {newSerialDilutionProperties, updatedTasks} = recoverSerialDilutionCardProperties(
        serialDilution[cardID],
        currentCard,
        tasks,
      );

      newCards[cardID] = newSerialDilutionProperties;
      newTasks = updatedTasks;
    } else if (currentCard.type === CardType.NORMALIZATION) {
      newCards[cardID] = recoverNormalizationCardProperties(normalization[cardID], currentCard, labwareState);
    } else if (currentCard.type === CardType.POOLING) {
      newCards[cardID] = recoverPoolingCardProperties(currentCard, tasks, labwareState);
    }

    if (
      currentCard.type === CardType.BROADCAST ||
      currentCard.type === CardType.POOLING ||
      currentCard.type === CardType.MIXING
    ) {
      newCards[cardID].reuseTips = isReuseTips;
    }

    // remove any subType that may exists in cards other than simple transfer
    delete newCards[cardID].subType;
  });

  return {cards: newCards, tasks: newTasks};
};

const initializeCardFromCSV = (currentCard, taskType, cardsState) => {
  const updatedCard = {...currentCard};

  switch (taskType) {
    case TaskType.USER_CONFIRMATION:
    case TaskType.USER_CONFIRMATION_EJECT:
      updatedCard.type = CardType.CONFIRMATION;
      break;
    case TaskType.PICK_TIP:
    case TaskType.ASPIRATE:
      updatedCard.type = CardType.SIMPLE_TRANSFER;
      break;
    default: {
      updatedCard.type = taskType;
    }
  }

  updatedCard.name = currentCard.name || getNewCardName(cardsState, updatedCard.type);

  return {updatedCurrentCard: cloneAndMapObject(updatedCard, CARD_DEFAULT_PROPERTIES, true)};
};

const createGroupStateConverter = () => {
  let groups = {};

  const getCardsAfterConvertingGroup = (currentRowData, cards) => {
    const {currentRowGroupData, currentRowCardData} = currentRowData || {};

    const {ID: groupID} = currentRowGroupData || {};
    const {ID: cardID} = currentRowCardData;

    const udpatedGroupState = {...groups};
    const updatedCardState = {...cards};

    if (!groups[groupID]) {
      udpatedGroupState[groupID] = {...currentRowGroupData, cards: [cardID]};
    } else {
      const currentGroupCardList = udpatedGroupState[groupID].cards;
      const isCardIDExists = currentGroupCardList.includes(cardID);

      if (!isCardIDExists) {
        udpatedGroupState[groupID].cards.push(cardID);
      }
    }

    updatedCardState[cardID].groupID = groupID;
    groups = udpatedGroupState;

    return updatedCardState;
  };

  const getGroups = () => ({...groups});

  return {
    getCardsAfterConvertingGroup,
    getGroups,
  };
};

const createCardStateConverter = (newLabwareState, setCardIDMaually, startingCardID = 0) => {
  let cards = {};
  let lastCardId = startingCardID;

  const singleTaskCard = [
    TaskType.USER_CONFIRMATION,
    TaskType.USER_CONFIRMATION_EJECT,
    TaskType.DELAY,
    TaskType.AIR_GAP,
    TaskType.MIXING,
  ];

  const simpleTransferCardTasks = [TaskType.PICK_TIP, TaskType.ASPIRATE];

  const newCardTypesList = [...singleTaskCard, ...simpleTransferCardTasks];

  const getCardSubType = (currentTask) => {
    const {type, labwareID} = currentTask;

    if (type === TaskType.ASPIRATE || type === TaskType.DISPENSE) {
      const taskLabware = Object.values(newLabwareState).find((labware) => labware.LabwareID === labwareID);

      if (taskLabware?.barcode) return SubCardType.PICKLIST;
    }

    return null;
  };

  const convertCard = (currentRowCardData, currentTask, skipIncrementLastId) => {
    const {type: taskType, ID: taskID, isIgnored: isTaskIgnored} = currentTask;

    const cardID = setCardIDMaually ? lastCardId : currentRowCardData?.ID;

    let updatedCurrentCard = cards[cardID] ? {...cards[cardID]} : null;

    const isNewCard = newCardTypesList.includes(taskType) && !updatedCurrentCard;

    if (isNewCard) {
      ({updatedCurrentCard} = initializeCardFromCSV(currentRowCardData, taskType, cards));
      if (!setCardIDMaually) lastCardId = cardID;
    }

    const isIncrementLastId =
      setCardIDMaually &&
      !skipIncrementLastId &&
      ((singleTaskCard.includes(taskType) && isNewCard) || taskType === TaskType.EJECT_TIP);

    if (isIncrementLastId) lastCardId += 1;

    const {tasks, isIgnored, subType} = updatedCurrentCard || {};

    updatedCurrentCard = {
      ...updatedCurrentCard,
      ID: cardID,
      subType: subType || getCardSubType(currentTask, newLabwareState),
      tasks: tasks ? [...tasks, taskID] : [taskID],
      isIgnored: isIgnored || (taskType === TaskType.PICK_TIP && isTaskIgnored),
    };

    cards = {...cards, [cardID]: updatedCurrentCard};
    return {card: updatedCurrentCard, isNewCard};
  };

  const getCards = () => ({...cards});
  const setCards = (newCards) => {
    cards = {...newCards};
  };
  const getLastCardId = () => lastCardId;

  return {
    convertCard,
    getCards,
    setCards,
    getLastCardId,
  };
};

export const convertCsvToCardState = (
  csvContent,
  // eslint-disable-next-line default-param-last
  availableLabwareState = {},
  cardObject,
  optionalParam = {startingCardId: 0, startingTaskId: 0, oldLabwareState: null},
) => {
  console.log('convertCsvToCardState');
  const csvRows = parse(csvContent, {delimiter: '\n'})?.data;

  const {startingCardId, startingTaskId, oldLabwareState} = optionalParam || {};
  const newLabwareState = {};
  const locationMap = {};
  const labwareCounterMap = {};

  const plateListRow = csvRows[0];

  const trayTypeKeyString = extractValueInPlateListRow(plateListRow, PlateListCell.TRAY_TYPE);
  const trayType = findKeyByValue(TrayTypeKey, (val) => val === Number(trayTypeKeyString));

  const plateList = plateListRow.slice(1, 19);

  plateList.forEach((item, i) => {
    if (i < 6) {
      const position = i + 1;
      const labwareId = dequote(item);
      const newLabware =
        (labwareId === '' ? {} : labwareId) in availableLabwareState ? jsonCopy(availableLabwareState[labwareId]) : {};
      newLabware.isSelected = false;
      newLabware.originalName = newLabware.name || '';
      newLabware.isRenamed = false;

      if (position !== 2 && labwareId) {
        if (labwareId in labwareCounterMap) {
          labwareCounterMap[labwareId] += 1;
        } else {
          labwareCounterMap[labwareId] = 0;
        }

        if (oldLabwareState && oldLabwareState[position].IDPrefix === labwareId) {
          newLabware.LabwareID = oldLabwareState[position].LabwareID;
          locationMap[position] = oldLabwareState[position].LabwareID;
        } else {
          newLabware.LabwareID = `${labwareId}0${labwareCounterMap[labwareId]}`;
          locationMap[position] = `${labwareId}0${labwareCounterMap[labwareId]}`;
        }

        if (labwareCounterMap[labwareId] >= 0) {
          newLabware.name =
            dequote(parseToStringLiteral(plateList[6 + i])) || `${newLabware.name} (${labwareCounterMap[labwareId]})`;
        }

        const barcode = dequote(parseToStringLiteral(plateList[12 + i]));
        if (barcode !== '') newLabware.barcode = barcode;
      }

      newLabwareState[position] = newLabware;
    }
  });

  let tasks = {};

  let taskNumber = 0;

  csvRows.splice(0, 1);

  let largestId = 0;

  const idCellKeyIndex = CSV_CELL_KEYS.findIndex((key) => key === CsvCellKeysEnum.ID);

  if (idCellKeyIndex) {
    const taskIds = csvRows
      .map((e) => dequote(e[idCellKeyIndex]))
      // eslint-disable-next-line no-restricted-globals
      .filter((e) => !isNaN(e) && Number.isInteger(parseInt(e, 10)))
      .sort()
      .reverse();

    largestId = taskIds.length > 0 ? taskIds[0] + 1 : 0;
    largestId += startingTaskId;
  }

  const isCardObjectExists = !_.isEmpty(cardObject?.card);
  const GroupsConverter = createGroupStateConverter();
  const CardsConverter = createCardStateConverter(newLabwareState, !isCardObjectExists, startingCardId);

  csvRows.forEach((cells, idx) => {
    if (cells.length <= 1) {
      return;
    }

    taskNumber += 1;

    const task = {ID: null, number: taskNumber};
    const currentRowGroupData = {ID: null, name: null};
    const currentRowCardData = {ID: null, name: '', isIgnored: false, type: ''};

    const currentTaskType = dequote(cells[0]);

    // flag initial value is true. will be false when there's active task present in one card
    let isCardIgnoredFlag = true;

    CSV_CELL_KEYS.forEach((cellKey, cellIndex) => {
      const value = dequote(cells[cellIndex]) || '';

      const isValueTrue = value?.toLowerCase() === 'true';
      const isValueEmpty = value === '' || !value;
      const valueInNumber = !isValueEmpty && _.isFinite(Number(value)) ? Number(value) : 0;

      if (isIgnoredCell(cellKey, currentTaskType)) {
        return;
      }

      if (!value && !ALLOWED_EMPTY_VALUE.includes(cellKey)) return;

      switch (cellKey) {
        case CsvCellKeysEnum.LOCATION: {
          const isLocationOnCaddy = isInsertLocationOnCaddy(valueInNumber, trayType);
          const isLocationOnWasteBin = valueInNumber === WASTE_BIN_LABWARE_ID;

          if (isLocationOnCaddy || isLocationOnWasteBin) {
            task.labwareID = valueInNumber;
            break;
          }

          task.labwareID = `${locationMap[valueInNumber]}`;
          break;
        }
        case 'rowTipCount':
        case 'columnTipCount':
        case 'delayTime':
        case 'column':
          if (value) {
            if (currentTaskType === TaskType.ENABLE_HUMIDIFIER && cellKey === CsvCellKeysEnum.DELAY_TIME) {
              task.humidifierTimeout = value;
            } else {
              task[cellKey] = valueInNumber;
            }
          }
          break;
        case 'tipTouchOffDelay':
          task[cellKey] = valueInNumber;
          break;
        case 'ID':
          {
            const newId = parseInt(value, 10);
            task[cellKey] = Number.isInteger(newId) ? newId + startingTaskId : (largestId += 1);
          }
          break;
        case 'mixingCycle':
          task.enableMixing = !!Number(value);
          task[cellKey] = task.enableMixing ? valueInNumber : 3;
          break;
        case 'volume':
        case 'tipPosition':
        case 'speed':
        case 'retractSpeed':
        case 'mixingSpeed':
        case CsvCellKeysEnum.MIXING_POSITION_ASPIRATE:
          if (!isValueEmpty) {
            if (currentTaskType === TaskType.ENABLE_HUMIDIFIER && cellKey === CsvCellKeysEnum.VOLUME) {
              task.humidityLevel = value;
            } else {
              task[cellKey] = valueInNumber;
            }
          }
          break;
        case 'mixingVolume':
          task[cellKey] = task.enableMixing || (value && value > 0) ? valueInNumber : Number(task.volume);
          break;
        case 'isIgnored':
          task[cellKey] = isValueTrue;
          isCardIgnoredFlag = isCardIgnoredFlag && isValueTrue;
          break;
        case 'dispenseAtWellSide':
          if (!isValueEmpty && currentTaskType === TaskType.DISPENSE && isValueTrue) {
            const deckInsertNumber = dequote(cells[1]);
            const labware = newLabwareState[deckInsertNumber];
            task[CsvCellKeysEnum.TIP_X_POSITION_OFFSET] = getMaxWellTipPositionValue(labware)?.x;
            task[CsvCellKeysEnum.TIP_Y_POSITION_OFFSET] = 0;
          }
          task[cellKey] = false;
          break;
        case 'precisionDispense':
        case 'tipTouchOff':
          if (!isValueEmpty) task[cellKey] = isValueTrue;
          break;
        case 'row':
          if (currentTaskType === TaskType.EJECT_TIP || currentTaskType === TaskType.PICK_TIP) {
            task[cellKey] = '';
          } else {
            task[cellKey] = value;
          }
          break;
        case 'isSimpleTransfer': {
          const isSimpleTransferCard = isValueTrue;
          currentRowCardData.type = isSimpleTransferCard ? CardType.SIMPLE_TRANSFER : null;
          break;
        }
        case 'cardID':
          currentRowCardData.ID = valueInNumber;
          break;
        case 'cardName':
          currentRowCardData.name = parseToStringLiteral(value);
          break;
        case 'changeTip':
          if (currentTaskType !== TaskType.PICK_TIP) task[cellKey] = isValueTrue;
          break;
        case 'groupID':
          currentRowGroupData.ID = isValueEmpty ? null : valueInNumber;
          break;
        case 'groupName':
          currentRowGroupData.name = parseToStringLiteral(value);
          break;
        case 'message': {
          const msg = parseToStringLiteral(value);
          if (msg !== '') task[cellKey] = msg;
          break;
        }
        case 'labwareIndex':
          if (value !== '') task[cellKey] = valueInNumber;
          break;
        case CsvCellKeysEnum.MIXING_POSITION_DISPENSE: {
          if (!value) task[cellKey] = task[CsvCellKeysEnum.MIXING_POSITION_ASPIRATE];
          else task[cellKey] = valueInNumber;
          break;
        }
        case CsvCellKeysEnum.TIP_X_POSITION_OFFSET:
        case CsvCellKeysEnum.TIP_Y_POSITION_OFFSET:
          task[cellKey] = valueInNumber;
          break;
        case CsvCellKeysEnum.TIP_TOUCH_OFF_HEIGHT:
        case CsvCellKeysEnum.TIP_TOUCH_OFF_X_DIRECTION:
        case CsvCellKeysEnum.TIP_TOUCH_OFF_Y_DIRECTION:
          task[cellKey] = valueInNumber;
          break;
        default:
          task[cellKey] = value;
          break;
      }
    });

    currentRowCardData.isIgnored = isCardIgnoredFlag;

    const isLastCard = idx === csvRows.length - 1;
    const {card, isNewCard} = CardsConverter.convertCard(currentRowCardData, task, isLastCard);

    if (isNewCard) {
      task.number = 1;
      taskNumber = 1;
    }

    if (_.isNumber(currentRowGroupData.ID)) {
      const rowData = {currentRowGroupData, currentRowCardData: card};
      const cards = CardsConverter.getCards();
      const updatedCard = GroupsConverter.getCardsAfterConvertingGroup(rowData, cards);

      CardsConverter.setCards(updatedCard);
    }

    tasks[task.ID] = task;
  });

  const groups = GroupsConverter.getGroups();
  let cards = CardsConverter.getCards();

  if (isCardObjectExists) {
    ({cards, tasks} = getCardStateFromCardObject(cardObject, cards, tasks, newLabwareState));
  }

  if (Object.keys(cards).length > 0) {
    const lastCardId = CardsConverter.getLastCardId();
    const lastCard = cards[lastCardId] || cards[lastCardId - 1];
    lastCard.isSelected = true;
    lastCard.isExpanded = true;

    if (_.isNumber(lastCard.groupID)) {
      const lastCardGroup = groups[lastCard.groupID];
      lastCardGroup.isSelected = true;
      lastCardGroup.isExpanded = true;
    }
  }

  return {
    labwareState: newLabwareState,
    cardState: {
      cards,
      tasks,
      groups,
    },
  };
};
